import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';
import type { QuizStatus, QuizView } from '@/types/api';

/**
 * Admin-only quiz authoring API. Metadata and the full question set are
 * managed via separate endpoints to keep each save transactional —
 * mirrors the server split.
 */

export interface QuizOptionPayload {
  id: string;
  text: string;
  emoji?: string | null;
}

export interface QuizQuestionPayload {
  prompt: string;
  /** Optional in line with the server DTO: pass null or omit to leave
   *  the explanation blank. */
  explanation?: string | null;
  callback?: string | null;
  options: QuizOptionPayload[];
  correctOptionId: string;
}

export interface CreateQuizPayload {
  bookId?: string | null;
  articleId?: string | null;
  /** 1..5; omitted defaults to 4 on the server. */
  passThreshold?: number;
  status?: QuizStatus;
  questions: QuizQuestionPayload[];
}

export interface UpdateQuizPayload {
  passThreshold?: number;
  status?: QuizStatus;
}

export interface ReplaceQuestionsPayload {
  questions: QuizQuestionPayload[];
}

export const adminQuizzesService = {
  async getForBook(bookId: string): Promise<QuizView | null> {
    return apiRequest<QuizView | null>(apiRoutes.adminQuizzes.forBook(bookId), {
      method: 'GET',
    });
  },

  async getForArticle(articleId: string): Promise<QuizView | null> {
    return apiRequest<QuizView | null>(apiRoutes.adminQuizzes.forArticle(articleId), {
      method: 'GET',
    });
  },

  async getById(id: string): Promise<QuizView> {
    return apiRequest<QuizView>(apiRoutes.adminQuizzes.one(id), { method: 'GET' });
  },

  async create(payload: CreateQuizPayload): Promise<QuizView> {
    return apiRequest<QuizView>(apiRoutes.adminQuizzes.create, {
      method: 'POST',
      body: payload,
    });
  },

  async updateMetadata(id: string, payload: UpdateQuizPayload): Promise<QuizView> {
    return apiRequest<QuizView>(apiRoutes.adminQuizzes.one(id), {
      method: 'PATCH',
      body: payload,
    });
  },

  async replaceQuestions(id: string, payload: ReplaceQuestionsPayload): Promise<QuizView> {
    return apiRequest<QuizView>(apiRoutes.adminQuizzes.questions(id), {
      method: 'PUT',
      body: payload,
    });
  },

  async remove(id: string): Promise<void> {
    await apiRequest<void>(apiRoutes.adminQuizzes.one(id), { method: 'DELETE' });
  },
};
